import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { appStore } from '@databases/redux/store';
import { type DatabaseView } from '@databases/types/database/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';

export type DatabaseAifcAnalyticsSnapshot = {
  elementKeys: string[];
};

type DatabaseDraft = ReturnType<typeof selectDatabaseDraftByContentId>;

const getCellKeysForEntries = (
  databaseDraft: DatabaseDraft,
  entryIds: string[],
  allowedFieldIdsByEntry?: Map<string, Set<string>>
): string[] => {
  const keys: string[] = [];
  for (const entryId of entryIds) {
    const values = databaseDraft.entriesById[entryId]?.values ?? {};
    const allowedFieldIds = allowedFieldIdsByEntry?.get(entryId);
    for (const fieldId of Object.keys(values)) {
      if (allowedFieldIds !== undefined && !allowedFieldIds.has(fieldId)) {
        continue;
      }
      // If value is not empty (allow 0 and false), add the cell to the keys
      if (values[fieldId].value != null && values[fieldId].value !== '') {
        keys.push(`cell:${entryId}:${fieldId}`);
      }
    }
  }
  return keys;
};

const getViewElementKeys = (databaseViews: DatabaseView[]): string[] => {
  const keys: string[] = [];
  for (const view of databaseViews) {
    const viewId = view?.id;
    if (!viewId) {
      continue;
    }

    // add each view as one element
    keys.push(`view:${viewId}`);

    // each sort on field counts as one element
    for (const sort of view.settings?.sorts ?? []) {
      keys.push(`view-setting:${viewId}:sort:${sort.fieldId}`);
    }

    // each filter on a view counts as one element
    if (view.settings?.filter) {
      keys.push(`view-setting:${viewId}:filter:${view.settings.filter.id}`);
    }

    // each hidden field counts as one element
    for (const [fieldId, visible] of Object.entries(view.settings?.fields ?? {})) {
      if (visible === false) {
        keys.push(`view-setting:${viewId}:hidden-field:${fieldId}`);
      }
    }
  }

  return keys;
};

export const buildDatabaseAifcAnalyticsSnapshot = (
  contentId: string
): DatabaseAifcAnalyticsSnapshot => {
  const state = appStore.getState();
  const databaseDraft = selectDatabaseDraftByContentId(state, {
    contentId,
    includeDeleted: false,
  });
  const database = selectDatabaseByContentIdRequired(state, { contentId });

  const elementKeys = [
    ...getCellKeysForEntries(databaseDraft, databaseDraft.entries),
    ...getViewElementKeys(database.views),
  ];

  return { elementKeys };
};

export const getRetainedElementCount = (
  baselineSnapshot: DatabaseAifcAnalyticsSnapshot,
  currentSnapshot: DatabaseAifcAnalyticsSnapshot
): number => {
  const currentElements = new Set(currentSnapshot.elementKeys);
  return baselineSnapshot.elementKeys.filter((elementKey) =>
    currentElements.has(elementKey)
  ).length;
};

export const buildScopedEditSnapshot = (
  contentId: string,
  editedFieldNamesByEntry: Map<string, Set<string>>,
  preExistingEntryIds: Set<string>,
  affectedViewNames: Set<string>
): DatabaseAifcAnalyticsSnapshot => {
  const state = appStore.getState();
  const databaseDraft = selectDatabaseDraftByContentId(state, {
    contentId,
    includeDeleted: false,
  });
  const database = selectDatabaseByContentIdRequired(state, { contentId });

  const fieldNameToId = new Map<string, string>();
  for (const [fieldId, field] of Object.entries(database.fieldsById)) {
    fieldNameToId.set(field.name, fieldId);
  }

  const editedFieldIdsByEntry = new Map<string, Set<string>>();
  for (const [entryId, fieldNames] of editedFieldNamesByEntry) {
    const fieldIds = new Set<string>();
    for (const fieldName of fieldNames) {
      const fieldId = fieldNameToId.get(fieldName);
      if (fieldId !== undefined) {
        fieldIds.add(fieldId);
      }
    }
    editedFieldIdsByEntry.set(entryId, fieldIds);
  }

  const editedEntryIds = [...editedFieldNamesByEntry.keys()];
  const newEntryIds = databaseDraft.entries.filter(
    // prevents newly added entry that is also edited from being double-counted
    (id: string) => !preExistingEntryIds.has(id) && !editedFieldNamesByEntry.has(id)
  );

  const cellKeys = [
    ...getCellKeysForEntries(databaseDraft, editedEntryIds, editedFieldIdsByEntry),
    ...getCellKeysForEntries(databaseDraft, newEntryIds),
  ];

  const affectedViews = database.views.filter((view) => affectedViewNames.has(view.name));
  const viewSettingKeys = getViewElementKeys(affectedViews);

  return { elementKeys: [...cellKeys, ...viewSettingKeys] };
};
